<template>
  <div class="payment-method-item" v-if="method.data">
    <div class="payment-method-item__wrap">
      <div class="payment-method-item__left-wrap">
        <div class="payment-method-item__icon">
          <PaymentMethodCardBrand v-if="method.data" :brand="method.data.brand.toLowerCase()" />
        </div>
        <div class="payment-method-item__name">
          <span>{{ method.title }}</span>
          <VTooltip bottom nudge-right="50">
            <template v-slot:activator="{ on, attrs }">
              <VIcon
                v-if="method.failedAt"
                class="payment-method-item__error-icon"
                v-bind="attrs"
                v-on="on"
                color="#EA0D0D"
              >
                mdi-alert
              </VIcon>
            </template>
            <span>{{ $t('failed',{failedAt: formatDateDefault(method.failedAt)}) }}</span>
          </VTooltip>
        </div>
        <div class="payment-method-item__expiration" v-if="method.data">
          <span class="d-none d-lg-inline">{{ $t('expiration') }}:</span>
          {{ expiration }}
        </div>
        <div class="payment-method-item__activity">
          <span v-if="method.active" class="payment-method-item__active-card">
            {{ $t('active') }}
          </span>
          <span v-else class="payment-method-item__inactive-card">
            {{ $t('inactive') }}
          </span>
          <AppWithLoading v-slot:default="{loader}" class="payment-method-item__active-btn">
            <button v-if="!method.active" @click="loader(() => setActive(method.id))" class="payment-method-item__activate">
              {{ $t('activate') }}
            </button>
          </AppWithLoading>
        </div>
      </div>
      <div class="payment-method-item__right-wrap">
        <button v-if="account.driver === paymentDrivers.STRIPE" @click="openEdit()" class="payment-method-item__change">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M3 17.2501V21.0001H6.75L17.81 9.94006L14.06 6.19006L3 17.2501ZM20.71 7.04006C21.1 6.65006 21.1 6.02006 20.71 5.63006L18.37 3.29006C17.98 2.90006 17.35 2.90006 16.96 3.29006L15.13 5.12006L18.88 8.87006L20.71 7.04006Z" fill="#757575" />
          </svg>
          <!--          {{ $t('edit') }}-->
        </button>
        <AppDialog width="560" hide-close-button v-model="editDialog" content-class="payment-method-edit-dialog-content">
          <div v-if="editDialog" class="payment-method-edit-dialog">
            <button type="button" class="payment-method-edit-dialog__close" @click="closeEditDialog">
              <VIcon size="20">mdi-close</VIcon>
            </button>
            <div class="payment-method-edit-dialog__content">
              <div class="payment-method-edit-dialog__title">{{ $t('editCardInformation') }}</div>
              <AppWithLoading v-slot:default="{loader}">
                <PaymentMethodCardAddress v-model="address" class="payment-method-edit-dialog__form">
                  <template v-slot:button="{validate}">
                    <div class="payment-method-edit-dialog__actions">
                      <button type="button" class="payment-method-edit-dialog__btn payment-method-edit-dialog__btn_cancel" @click="closeEditDialog">
                        {{ $t('cancelButton') }}
                      </button>
                      <button type="button" class="payment-method-edit-dialog__btn payment-method-edit-dialog__btn_save" @click="saveWithValidation(validate, loader)">
                        {{ $t('saveButton') }}
                      </button>
                    </div>
                  </template>
                </PaymentMethodCardAddress>
              </AppWithLoading>
            </div>
          </div>
        </AppDialog>
        <AppDialog width="660" v-model="dialog">
          <template v-slot:activator="{on, attrs}">
            <button v-on="on" v-bind="attrs" class="payment-method-item__delete">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19ZM19 4H15.5L14.5 3H9.5L8.5 4H5V6H19V4Z" fill="#757575" />
              </svg>
              <!--            {{ $t('delete') }}-->
            </button>
          </template>
          <div class="payment-method-delete">
            <AppWithLoading v-slot:default="{loader}">
              <template>
                <div class="payment-method-delete__icon">
                  <PaymentMethodCardBrand :brand="method.data.brand.toLowerCase()" />
                </div>

                <template v-if="!canDelete">
                  <div class="payment-method-delete__title">
                    {{ $t('cantDelete') }}
                  </div>
                  <div class="payment-method-delete__text">
                    {{ $t('cantDeleteText',{card: method.title}) }}
                  </div>
                </template>
                <template v-else>
                  <div class="payment-method-delete__title">
                    {{ $t('deleteCard') }} {{ method.title }}?
                  </div>
                </template>
                <div class="payment-method-delete__buttons">
                  <button v-if="canDelete" @click="loader(()=>remove())" class="payment-method-delete__btn payment-method-delete__btn_delete">
                    {{ $t('delete') }}
                  </button>
                  <button @click="dialog = false" class="payment-method-delete__btn">
                    {{ $t('cancel') }}
                  </button>
                </div>
              </template>
            </AppWithLoading>
          </div>
        </AppDialog>
      </div>
    </div>
  </div>
</template>

<script>
import {
  computed, ref, toRefs, watch,
} from '@vue/composition-api';
import useDateFormatter from '@/compositions/useDateFormatter';
import { useI18n } from 'vue-i18n-composable';
import PaymentMethodCardAddress from '@/components/Payment/Methods/PaymentMethodCardAddress';
import usePaymentMethods from '@/compositions/accounting/usePaymentMethods';
import AppWithLoading from '@/components/AppWithLoading';
import useSubscription from '@/compositions/accounting/useSubscription';
import PaymentMethodCardBrand from '@/components/Payment/Methods/PaymentMethodCardBrand';
import AppDialog from '@/components/AppDialog';
import { paymentDrivers } from '@/enums';
import useAccount from '@/compositions/accounting/useAccount';

export default {
  name: 'PaymentMethodsListItem',
  components: {
    AppDialog, PaymentMethodCardBrand, AppWithLoading, PaymentMethodCardAddress,
  },
  props: {
    method: {
      type: Object,
      required: true,
    },
    openedId: {
      type: Number,
      required: false,
      default: null,
    },
  },
  setup(props, { emit, root }) {
    const { method, openedId } = toRefs(props);
    const { account } = useAccount();

    const isCurrentEdit = ref(false);

    const { formatDateDefault } = useDateFormatter();
    const { t } = useI18n();
    const editDialog = ref(false);
    const address = ref({ ...method.value?.data?.address });
    const { deleteMethod, setActive, updateData } = usePaymentMethods();

    const dialog = ref(false);

    const active = ref(method.value.active);

    watch(openedId, (newOpenedId) => {
      isCurrentEdit.value = method.value?.id === newOpenedId;
      if (!isCurrentEdit.value) {
        editDialog.value = false;
      }
    });

    watch(editDialog, (newValue) => {
      if (!newValue && openedId.value === method.value?.id) {
        emit('edit', null);
      }
    });

    function openEdit() {
      if (!editDialog.value) {
        address.value = { ...method.value?.data?.address };
        emit('edit', method.value?.id);
        editDialog.value = true;
        return;
      }

      closeEditDialog();
    }

    function closeEditDialog() {
      emit('edit', null);
      editDialog.value = false;
    }

    const { subscriptionStatus } = useSubscription();
    const canDelete = computed(() => !subscriptionStatus.value || !method.value.active);

    async function remove() {
      await deleteMethod(method.value.id);
      dialog.value = false;
      emit('fetchData');
    }

    async function save() {
      await updateData(method.value.id, { address: address.value });
      editDialog.value = false;
      emit('fetchData');
      root.$toast.success(t('cardInfoUpdated'));
    }

    function saveWithValidation(validate, loader) {
      if (!validate()) {
        root.$toast.warning(t('fillRequiredFields'));
        return;
      }

      loader(async () => {
        await save();
      });
    }

    const expiration = computed(() => {
      if (method.value.paymentSystem.driver === paymentDrivers.STRIPE) {
        return `${method.value.data.exp_month}/${method.value.data.exp_year}`;
      }
      if (method.value.paymentSystem.driver === paymentDrivers.ALFABANK) {
        const expirationData = method.value.data.expiration;
        return `${expirationData.substring(0, 4)}/${expirationData.substring(4, 6)}`;
      }
    });

    return {
      active,
      formatDateDefault,
      editDialog,
      address,
      remove,
      setActive,
      dialog,
      canDelete,
      save,
      saveWithValidation,
      openEdit,
      closeEditDialog,
      isCurrentEdit,
      expiration,
      account,
      paymentDrivers,
    };
  },
};
</script>

<style lang="scss" scoped>
  .payment-method-item {
    border: 1px solid #E0E0E0;
    border-radius: 0.5rem;
    font-size: 1.0625rem;
    font-weight: 400;
    line-height: 1.53;
    color: #091A35;
    position: relative;
    margin-bottom: 1.25rem;
    display: flex;
    align-items: center;
    min-height: 100px;
    padding: 0 1.5rem;
    transition: box-shadow 0.2s ease, border-color 0.2s ease;
    &:hover {
      border-color: #D4D9DE;
      box-shadow: 0 0.375rem 1rem rgba(9, 26, 53, 0.06);
    }
    &__wrap {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
    }
    &__left-wrap {
      display: flex;
      align-items: center;
    }
    &__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 4.0625rem;
      height: 3.25rem;
      background: rgba(238, 238, 238, 0.4);
      border: 1px solid #EEEEEE;
      border-radius: 0.5rem;
      line-height: 0;
      margin-right: 1.625rem;
      svg {
        max-width: 100%;
        max-height: 100%;
      }
    }
    &__name{
      display: flex;
      align-items: center;
      width: 9.75rem;
      margin-right: 1.625rem;
      i {
        margin-bottom: 3px;
        margin-left: 3px;
      }
    }
    &__expiration {
      width: 10.5rem;
    }
    &__activity {
      display: flex;
      align-items: center;
    }
    &__active-card {
      display: flex;
      align-items: center;
      margin-right: 1.125rem;
      &:before {
        content: '';
        display: inline-block;
        width: 0.5rem;
        height: 0.5rem;
        border-radius: 50%;
        background: #1EC658;
        margin-right: 0.6875rem;
      }
    }
    &__inactive-card {
      display: flex;
      align-items: center;
      margin-right: 1.125rem;
      &:before {
        content: '';
        display: inline-block;
        width: 0.5rem;
        height: 0.5rem;
        border-radius: 50%;
        background: #CCCCCC;
        margin-right: 0.6875rem;
      }
    }
    &__activate {
      color: #1F85D7;
      transition: color 0.2s ease;
      &:hover {
        color: #146db3;
      }
    }
    &__change {
      margin-right: 1.25rem;
      line-height: 1;
      transition: transform 0.15s ease, opacity 0.2s ease;
      &:hover {
        opacity: 0.85;
        transform: scale(1.04);
      }
    }
    &__delete {
      line-height: 1;
      transition: transform 0.15s ease, opacity 0.2s ease;
      &:hover {
        opacity: 0.85;
        transform: scale(1.04);
      }
    }
    &__edit {
      max-width: 35rem;
      padding-top: 1.875rem;
    }
  }
  .payment-method-delete {
    background: #FFFFFF;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.04);
    border-radius: 0.625rem;
    text-align: center;
    padding: 2.125rem 2rem 3rem;
    &__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 8.25rem;
      height: 8.25rem;
      border: 1rem solid #fafafa;
      background: #E5E5E5;
      border-radius: 50%;
      margin: 0 auto 1.25rem auto;
    }
    &__title {
      font-size: 2.125rem;
      line-height: 1.32;
      font-weight: 400;
      color: #091A35;
    }
    &__text {
      font-size: 1.0625rem;
      line-height: 1.53;
      color: #091A35;
      margin-top: 1.125rem;
    }
    &__buttons {
      margin-top: 1.875rem;
    }
    &__btn {
      width: 15rem;
      padding: 0.8125rem 1.125rem;
      letter-spacing: 0.02em;
      color: #000000;
      font-weight: 400;
      border: 1px solid #D6D6D6;
      border-radius: 0.25rem;
      &:first-child {
        margin-right: 1.25rem;
      }
      &_delete {
        color: #EA0D0D;
        border-color: #EA0D0D;

      }
    }
  }
  .payment-method-edit-dialog {
    position: relative;
    background: #FFFFFF;
    box-shadow: none;
    overflow: hidden;
    &__close {
      position: absolute;
      top: 0.75rem;
      right: 0.75rem;
      width: 2rem;
      height: 2rem;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: #757575;
      z-index: 2;
      border-radius: 50%;
      transition: background-color 0.2s ease, color 0.2s ease;
      &:hover {
        background: rgba(117, 117, 117, 0.08);
        color: #5f5f5f;
      }
    }
    &__content {
      padding: 1.875rem;
    }
    &__title {
      font-size: 1.5rem;
      line-height: 1.33;
      font-weight: 400;
      color: #212121;
      margin-bottom: 2rem;
    }
    &__actions {
      display: flex;
      gap: 0.75rem;
    }
    &__btn {
      flex: 1 1 50%;
      width: 50%;
      height: 2.75rem;
      border-radius: 0.25rem;
      padding: 0 1.125rem;
      font-size: 1rem;
      font-weight: 400;
      line-height: 1;
      transition: transform 0.15s ease, box-shadow 0.2s ease, background-color 0.2s ease;
      &:hover {
        transform: translateY(-1px);
      }
      &_cancel {
        background: #F3F5F7;
        color: rgba(117, 117, 117, 1);
        &:hover {
          background: #E9EDF1;
          box-shadow: 0 0.25rem 0.75rem rgba(117, 117, 117, 0.18);
        }
      }
      &_save {
        background: rgba(31, 197, 127, 1);
        color: #FFFFFF;
        &:hover {
          background: #1ab06f;
          box-shadow: 0 0.375rem 0.875rem rgba(31, 197, 127, 0.3);
        }
      }
    }
    &__form {
      ::v-deep .v-input {
        margin-bottom: 0 !important;
      }
      ::v-deep .v-input:last-child {
        margin-bottom: 2rem !important;
      }
      ::v-deep .v-input .v-input__details {
        display: none !important;
      }
      ::v-deep .v-input .v-input__control .v-input__slot {
        min-height: 3.1875rem !important;
        height: 3.1875rem !important;
        border-radius: 0.25rem !important;
        background: #F3F5F7 !important;
        padding: 0 !important;
      }
      ::v-deep .v-input .v-input__control .v-input__slot input {
        color: rgba(0, 0, 0, 0.6) !important;
        font-size: 1rem !important;
        font-weight: 400 !important;
        height: 3.1875rem !important;
        padding: 1rem 0.75rem !important;
      }
      ::v-deep .v-input .v-input__control .v-input__slot .v-select__selections {
        color: rgba(0, 0, 0, 0.6) !important;
        font-size: 1rem !important;
        font-weight: 400 !important;
        min-height: 3.1875rem !important;
        height: 3.1875rem !important;
        padding: 1rem 0.75rem !important;
      }
      ::v-deep .v-input .v-input__control .v-input__slot .v-select__selection {
        color: rgba(0, 0, 0, 0.6) !important;
      }
      ::v-deep .v-input.v-select .v-input__control .v-input__slot,
      ::v-deep .v-input.v-autocomplete .v-input__control .v-input__slot,
      ::v-deep .v-input.v-select .v-input__control .v-input__slot input,
      ::v-deep .v-input.v-autocomplete .v-input__control .v-input__slot input,
      ::v-deep .v-input.v-select .v-input__control .v-input__slot .v-select__selections,
      ::v-deep .v-input.v-autocomplete .v-input__control .v-input__slot .v-select__selections {
        cursor: pointer !important;
      }
      ::v-deep .v-input.v-select .v-input__append-inner,
      ::v-deep .v-input.v-autocomplete .v-input__append-inner {
        align-self: center !important;
        display: flex !important;
        align-items: center !important;
        height: 100% !important;
        margin-top: 0 !important;
        padding-right: 0.75rem !important;
      }
      ::v-deep .v-input.v-select .v-input__append-inner .v-icon,
      ::v-deep .v-input.v-autocomplete .v-input__append-inner .v-icon {
        line-height: 1 !important;
      }
      ::v-deep .v-input .v-input__control .v-input__slot .v-label {
        color: rgba(0, 0, 0, 0.6) !important;
        font-size: 1rem !important;
        font-weight: 400 !important;
        padding-left: 12px;
      }
      ::v-deep .v-input .v-input__control .v-input__slot .v-label:not(.v-label--active) {
        top: auto !important;
      }
      ::v-deep .v-input .v-input__control .v-input__slot .v-label.v-label--active {
        padding-left: 20px;
      }
      ::v-deep .v-input.v-text-field--outlined .v-input__control .v-input__slot fieldset,
      ::v-deep .v-input.v-select--outlined .v-input__control .v-input__slot fieldset,
      ::v-deep .v-input.v-autocomplete--outlined .v-input__control .v-input__slot fieldset {
        border: none !important;
      }
    }
  }
  ::v-deep .payment-method-edit-dialog-content .v-dialog {
    overflow: hidden !important;
    box-shadow: none !important;
  }
  ::v-deep .payment-method-edit-dialog-content .v-dialog > .v-card {
    box-shadow: none !important;
  }
  @media #{map-get($display-breakpoints, 'md-and-down')} {
    .payment-method-item {
      padding: 1.25rem 1.25rem 0.5rem 1.25rem;
      font-size: 1rem;
      margin-bottom: 1rem;
      &:hover {
        box-shadow: none;
      }
      &__icon {
        width: 3.75rem;
        height: 3rem;
        padding: 0.5rem;
        margin-right: 1.5rem;
      }
      &__name{
        width: 5.5rem;
        margin-right: 1.5rem;
        i {
          font-size: 1.375rem;
        }
      }
      &__expiration {
        width: 8rem;
      }
      &__active-card {
        margin-right: 1rem;
      }
      &__inactive-card {
        margin-right: 1rem;
      }
      &__change {
        margin-right: 1rem;
        line-height: 1;
      }
      &__edit {
        padding-top: 1.75rem;
      }
    }
    .payment-method-delete {
      &__icon {
        width: 6.25rem;
        height: 6.25rem;
        padding: 0.5rem;
        border-width: 0.875rem;
      }
      &__title {
        font-size: 1.75rem;
      }
      &__btn {
        width: 12.5rem;
        padding: 0.75rem 1rem;
        margin: 0 auto;
        &:first-child {
          margin-right: 1.125rem;
        }
      }
    }
  }
  @media #{map-get($display-breakpoints, 'sm-and-down')} {
    .payment-method-edit-dialog {
      &__content {
        padding: 1.25rem;
      }
      &__title {
        font-size: 1.25rem;
        margin-bottom: 1.25rem;
      }
      &__btn {
        height: 3rem;
      }
    }
    .payment-method-item {
      padding: 0.9375rem 0.9375rem 0.5rem 0.9375rem;
      font-size: 0.875rem;
      margin-bottom: 0.9375rem;
      &__wrap {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 0.5rem;
        padding-right: 1.8125rem;
      }
      &__left-wrap {
        display: flex;
        align-items: center;
      }
      &__icon {
        width: 3.5rem;
        height: 2.8rem;
        margin-right: 1.25rem;
      }
      &__name{
        width: 4rem;
        margin-right: 1.25rem;
        i {
          font-size: 1.25rem;
        }
      }
      &__expiration {
        width: 5.5rem;
      }
      &__activity {
        display: flex;
        align-items: center;
        line-height: 1;
      }
      &__active-card {
        display: flex;
        align-items: center;
        margin-right: 1.125rem;
        &:before {
          content: '';
          display: inline-block;
          width: 0.5rem;
          height: 0.5rem;
          border-radius: 50%;
          background: #1EC658;
          margin-right: 0.6875rem;
        }
      }
      &__inactive-card {
        display: flex;
        align-items: center;
        margin-right: 1.125rem;
        &:before {
          content: '';
          display: inline-block;
          width: 0.5rem;
          height: 0.5rem;
          border-radius: 50%;
          background: #CCCCCC;
          margin-right: 0.6875rem;
        }
      }
      &__activate {
        color: #1F85D7;
      }
      &__change {
        margin-right: 0;
        position: absolute;
        top: 0.75rem;
        right: 0.875rem;
        svg {
          width: 1.125rem;
        }
      }
      &__delete {
        position: absolute;
        top: 2.75rem;
        right: 0.875rem;
        svg {
          width: 1.125rem;
        }
      }
      &__edit {
        padding-top: 1.25rem;
        margin-bottom: 0.5rem;
      }
    }
  }
  @media screen and (max-width: 730px) {
    .payment-method-item {
      padding: 0.75rem 0.75rem 0 0.9375rem;
      display: block;
      font-size: 0.8125rem;
      margin-bottom: 0.5rem;
      &__wrap {
        display: block;
        padding-right: 1.5rem;
        margin-bottom: 0.75rem;
      }
      &__left-wrap {
        display: block;
      }
      &__icon {
        display: inline-flex;
        width: auto;
        height: 1.875rem;
        background: transparent;
        border: none;
        max-width: 2.25rem;
        margin-right: 0.75rem;
        vertical-align: middle;
        padding: 0;
        margin-bottom: 0.5rem;
      }
      &__name{
        display: inline-block;
        width: auto;
        margin-right: 1rem;
        vertical-align: middle;
        margin-bottom: 0.5rem;
        i {
          font-size: 1rem;
        }
      }
      &__expiration {
        display: inline-block;
        width: auto;
        vertical-align: middle;
        margin-bottom: 0.5rem;
      }
      &__activity {
        display: block;
      }
      &__active-card {
        display: inline-block;
        vertical-align: middle;
        align-items: center;
        margin-right: 1.125rem;
        &:before {
          width: 0.5rem;
          height: 0.5rem;
          margin-right: 0.375rem;
        }
      }
      &__inactive-card {
        display: inline-block;
        vertical-align: middle;
        margin-right: 1rem;
        &:before {
          width: 0.5rem;
          height: 0.5rem;
          margin-right: 0.375rem;
        }
      }
      &__active-btn {
        display: inline-block;
        vertical-align: middle;
      }
      &__change {
        top: 0.625rem;
        right: 0.75rem;
        svg {
          width: 1.125rem;
        }
      }
      &__delete {
        top: 2.75rem;
        right: 0.65rem;
        svg {
          width: 1.125rem;
        }
      }
      &__edit {
        padding: 0.5rem 0 0.75rem;
      }
    }
  }
  @media #{map-get($display-breakpoints, 'xs-only')} {
    .payment-method-edit-dialog {
      &__content {
        padding: 1rem;
      }
      &__title {
        font-size: 1.125rem;
        margin-bottom: 1rem;
      }
      &__actions {
        flex-direction: column;
        gap: 0.625rem;
      }
      &__btn {
        width: 100%;
        flex: 1 1 auto;
      }
    }
    .payment-method-delete {
      padding: 1.875rem 1.25rem 2rem 1.25rem;
      &__icon {
        width: 5.25rem;
        height: 5.25rem;
        border-width: 0.75rem;
      }
      &__title {
        font-size: 1.25rem;
      }
      &__text {
        font-size: 0.9375rem;
        margin-top: 0.875rem;
      }
      &__buttons {
        margin-top: 2rem;
      }
      &__btn {
        display: block;
        width: 10rem;
        padding: 0.5rem 1rem;
        margin: 0 auto;
        &:first-child {
          margin-right: auto;
          margin-bottom: 1.25rem;
        }
      }
    }
  }
</style>

<i18n lang="yml" locale="ru">
  editCardInformation: Редактирование данных карты
  cancelButton: Отмена
  saveButton: Сохранить
  fillRequiredFields: Пожалуйста, заполните обязательные поля.
  cardInfoUpdated: Данные карты успешно обновлены.
  expiration: Срок
  active: Активна
  inactive: Не активна
  activate: Сделать активной
  edit: Редактировать
  save: Сохранить
  delete: Удалить
  deleteCard: Удалить карту
  cantDelete: Невозможно удалить карту
  cantDeleteText: Карта {card} используется для продления текущей подписки. Чтобы удалить эту карту, сначала необходимо установить другую карту в качестве активной.
  cancel: Закрыть
  failed: 'Неудачная попытка оплаты {failedAt}'
</i18n>
<i18n lang="yml" locale="en">
  editCardInformation: Edit card information
  cancelButton: Cancel
  saveButton: Save
  fillRequiredFields: Please fill in the required fields.
  cardInfoUpdated: Card information updated successfully.
  expiration: Expiration date
  active: Active
  inactive: Inactive
  activate: Make active
  edit: Edit
  save: Save
  delete: Delete
  deleteCard: Delete card
  cantDelete: Unable to delete card
  cantDeleteText: Card {card} is being used to extend your current subscription. In order to delete this card, you first need to choose a different one as your active card.
  cancel: Cancel
  failed: 'Failed charge attempt at {failedAt}'
</i18n>
